import { useEffect, useState } from "react";
import type { ReactNode } from "react";

interface UndoToastProps {
  readonly message: string;
  readonly onUndo: () => void;
  readonly onDismiss: () => void;
  readonly duration?: number;
  readonly actions?: ReactNode;
}

export function UndoToast({
  message,
  onUndo,
  onDismiss,
  duration = 4000,
  actions,
}: UndoToastProps) {
  const [progress, setProgress] = useState(100);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const start = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - start;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        setExiting(true);
        setTimeout(onDismiss, 200);
      }
    }, 50);
    return () => clearInterval(interval);
  }, [duration, onDismiss]);

  return (
    <div
      className={`fixed bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 z-50 ${exiting ? "animate-toast-exit" : "animate-slide-up"}`}
    >
      <div
        className="relative bg-neutral-900 text-neutral-50 px-4 py-2.5 rounded-xl shadow-xl flex items-center gap-3 min-w-72 max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-sm font-medium flex-1">{message}</span>
        {actions}
        <button
          type="button"
          onClick={onUndo}
          className="shrink-0 px-2.5 py-1 rounded-lg bg-accent/20 text-accent text-sm font-semibold
            hover:bg-accent/30 transition-colors focus:outline-none"
        >
          Annuler
        </button>
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-neutral-700">
          <div
            className="h-full bg-accent transition-all ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
}
